<template>
    <div id="information-list2">
        <div class="headtitle">{{ $t("message.userInfo") }}</div>
        <div>
            <div class="userInfo">
                <div class="userInfo first">
                    {{ $t("message.fullName") }}
                </div>
                <div class="userInfo sec">
                    <span class="ml-2 d-flex align-items-center">
                        <a-avatar
                            v-if="!!activeConversation.whatsappNumber"
                            :key="activeConversation._id"
                            :size="30"
                            :src="getVisitorAvatarSrc()"
                            :username="activeConversation.fullname || '-'"
                            class="mr-2"
                            shape="square"
                            style="background-color: #3899ff; border-radius: 7px !important"
                        >
                            {{ (activeConversation.fullname || "").substr(0, 1).toLocaleUpperCase() }}
                        </a-avatar>
                        <div
                            ref="visitorNameInput"
                            :class="{
                                active: visitorNameInputActive,
                            }"
                            class="visitorName disableShortcut darkSpan ml-1 ellipsis"
                            contentEditable="true"
                            @blur="setVisitorNameInputActive(false)"
                            @focus="setVisitorNameInputActive(true)"
                            @focusUserInput="focusVisitorNameInput()"
                            @keydown="handleVisitorNameInputKeyDown"
                        >
                            {{ activeConversation.fullname }}
                            <ActionEdit
                                v-tippy="{
                                    followCursor: true,
                                    placement: 'bottom-end',
                                    theme: isDarkMode ? 'light-border' : 'google',
                                }"
                                :content="$t('message.clickToEdit')"
                                height="11"
                                style="font-size: 10px !important"
                                width="11"
                            />
                        </div>
                    </span>
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">{{ $t("message.email") }}</div>
                <div class="userInfo sec">
                    <span class="ml-2 d-flex align-items-center" style="color: #3899ff">
                        <a-icon class="mr-2" type="mail" />
                        <div
                            ref="visitorEmailInput"
                            :class="{
                                active: visitorEmailInputActive,
                                'text-primary': activeConversation.email === '',
                            }"
                            class="visitorEmail disableShortcut"
                            contentEditable="true"
                            @blur="setVisitorEmailInputActive(false)"
                            @focus="setVisitorEmailInputActive(true)"
                            @focusUserInput="focusVisitorEmailInput()"
                            @keydown="handleVisitorEmailInputKeyDown"
                        >
                            {{ activeConversation.email === "" ? $t("message.addEmail") : activeConversation.email }}
                            <ActionEdit
                                v-tippy="{
                                    followCursor: true,
                                    placement: 'bottom-end',
                                    theme: isDarkMode ? 'light-border' : 'google',
                                }"
                                :content="$t('message.clickToEdit')"
                                height="11"
                                width="11"
                            />
                        </div>
                    </span>
                </div>
            </div>
            <div
                v-if="Array.isArray(activeConversation.customLoginInfos) && activeConversation.customLoginInfos.length"
            >
                <div v-for="(inputValue, index) of getCustomLoginInfos()" :key="index" class="userInfo">
                    <div class="userInfo first">{{ inputValue.name }}</div>
                    <div class="userInfo sec">{{ inputValue.value }}</div>
                </div>
            </div>
            <div v-else>
                <div v-if="getSubjectName(activeConversation.subject)" class="userInfo">
                    <div class="userInfo first">
                        {{ $t("message.department") }}
                    </div>
                    <div class="userInfo sec">
                        {{ getSubjectName(activeConversation.subject) }}
                    </div>
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">
                    {{ $t("message.representative") }}
                </div>
                <div class="userInfo sec" >
                    {{ getAgentNames(activeConversation).length > 15 ? getAgentNames(activeConversation).slice(0, 15) + '...' : getAgentNames(activeConversation) }}
                </div>
            </div>
            <div class="d-flex">
                <div class="userInfo">
                    <div class="userInfo first">
                        {{ $t("message.country") }}
                    </div>

                    <div
                        v-if="
                            getCountryCode(
                                $_.get(activeConversation, 'geoLocation.country') || activeConversation.languageCode
                            )
                        "
                        class="userInfo sec"
                        style="padding-top: 2px"
                    >
                        <country-flag
                            v-tippy="{
                                followCursor: true,
                                placement: 'bottom-end',
                                theme: isDarkMode ? 'light-border' : 'google',
                            }"
                            :content="
                                getCountryName(
                                    $_.get(activeConversation, 'geoLocation.country') || activeConversation.languageCode
                                )
                            "
                            :country="
                                getCountryCode(
                                    $_.get(activeConversation, 'geoLocation.country') || activeConversation.languageCode
                                )
                            "
                            size="normal"
                        />
                    </div>
                    <div
                        v-else
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="
                            getCountryName(
                                $_.get(activeConversation, 'geoLocation.country') || activeConversation.languageCode
                            )
                        "
                        class="userInfo sec"
                    >
                        {{ activeConversation.geoLocation ? activeConversation.geoLocation.country : "N/A" }}
                    </div>
                </div>

                <div class="userInfo pl-3">
                    <div class="userInfo first">
                        {{ $t("message.language") }}
                    </div>
                    <div class="userInfo sec" style="padding-top: 10px">
                        {{ activeConversation.languageCode }}
                    </div>
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">
                    {{ $t("message.chatLanguage") }}
                </div>
                <div class="userInfo sec" style="padding-top: 10px">
                    <select @change="updateVisitorLang" v-model="activeConversation.visitor.preferredLang">
                        <option v-for="lang in languages" :value="lang.code">{{ lang.originalName }}</option>
                    </select>
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">
                    {{ $t("message.visitingPage") }}
                </div>
                <div class="userInfo sec" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                    <a
                        v-if="activeConversation.visitingPage"
                        :href="activeConversation.visitingPage ? activeConversation.visitingPage : '#'"
                        class="info-label"
                        target="_blank"
                        >{{ activeConversation.visitingPage }}</a
                    >
                    <span v-else>N/A</span>
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">
                    {{ $t("message.osAndBrowser") }}
                </div>
                <div class="userInfo sec">
                    <div v-if="!activeConversation.whatsappNumber" class="userInfo sec">
                        <span v-if="activeConversation.visitor.os && activeConversation.visitor.browser"
                            >{{ activeConversation.visitor.os }} / {{ activeConversation.visitor.browser }}</span
                        >
                        <span v-else> {{ getUserDetailedLoginInfo(activeConversation.userAgent) }}</span>
                    </div>
                    <div v-else class="userInfo sec">whatsapp</div>
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">IP</div>
                <div class="userInfo sec">
                    {{ activeConversation.ipAddress }}
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">{{ $t("message.chatId") }}</div>
                <div class="userInfo sec">{{ activeConversation._id }}</div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">
                    {{ $t("message.customerClientVersion") }}
                </div>
                <div class="userInfo sec">
                    {{
                        (
                            this.activeConversation.visitor.version || {
                                version: -1,
                                versionStr: "N/A",
                                buildNumber: -1,
                                commitHash: "",
                            }
                        ).versionStr
                    }}
                </div>
            </div>
            <div class="userInfo">
                <div class="userInfo first">Note</div>
                <div class="userInfo sec">
                    <div class="rep-note-element">
                        <div class="rep-note-name">{{ noteSender }}</div>
                        <a-textarea v-model="info" class="disableShortcut" @focus="isEditingOpen = true" />
                        <div v-if="isEditingOpen" class="rep-note-actions">
                            <a-button-group size="small">
                                <a-button icon="close" type="danger" @click="notSaveInfo" />
                                <a-button icon="check" type="primary" @click="saveInfo" />
                            </a-button-group>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import Representative from "./svg/Representative";
import SendIcon from "./svg/SendIcon";
import ChatId from "./svg/ChatId";
import Operating from "./svg/Operating";
import World from "./svg/World";
import Ip from "./svg/Ip";
import User from "./svg/User";
import Mail from "./svg/Mail";
import KeywordShortCut from "./svg/KeywordShortCut";
import ConversationColor from "./svg/ConversationColor";
import { log2 } from "../utils/log2";
import CountryFlag from "vue-country-flag";
import countryCodes from "../utils/countryCodes";
import { i18n } from "../language";
import ClipboardList from "./svg/ClipboardList";
import Language from "./svg/Language";
import Earth from "./svg/Earth";
import UserName from "./svg/UserName";
import Version from "./svg/Version";
import { capitalize, validateEmail } from "../utils/utils";
import { sendSocketMessage } from "../utils/socketStream";
import ActionEdit from "./svg/ActionEdit";
import $_ from "lodash";
import CONFIG from "../config/config";
import { getPersistenceData } from "../utils/persistenceStorage";
import languages from "../language/languages.json";

Vue.component("country-flag", CountryFlag);
const log = log2("UserInfoWidget");
export default {
    name: "UserInfoWidget",
    components: {
        Representative,
        SendIcon,
        ChatId,
        Operating,
        World,
        Ip,
        User,
        Mail,
        KeywordShortCut,
        ConversationColor,
        ClipboardList,
        Language,
        Earth,
        UserName,
        Version,
        ActionEdit,
    },
    data() {
        return {
            languages,
            info: "",
            noteSender: "",
            isEditingOpen: false,
            conversationId: "",
            visitorEmailInputActive: false,
            visitorNameInputActive: false,
            $_,
        };
    },
    mounted() {
        if (this.activeConversation.note) {
            this.info = this.activeConversation.note.representativeNote;
            this.noteSender = this.activeConversation.note.representativeName;
            this.conversationId = this.activeConversation._id;
        }
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
        }),
        ...mapState({
            site: (state) => state.site.site,
            contacts: (state) => state.whatsapp.contacts,
            user: (state) => state.auth.user,
            users: (state) => state.user.users.data,
            activeConversation: (state) =>
                state.app.fakeMode ? state.chat.fakeActiveConversation : state.chat.activeConversation,
            subjects: (state) => state.subject.subjects.data,
            inputs: (state) =>
                state.customLoginForm.inputs.reduce((acc, cur) => {
                    acc[cur._id] = cur;
                    return acc;
                }, {}),
        }),
    },
    updated() {
        log("updated", this.activeConversation);
        if (this.conversationId !== this.activeConversation._id) {
            this.info = this.activeConversation.note.representativeNote;
            this.noteSender = this.activeConversation.note.representativeName;
            this.conversationId = this.activeConversation._id;
        }
    },
    created() {
        log("created", this.activeConversation);
    },
    methods: {
        ...mapActions({
            addNoteToConversation: "chat/addNoteToConversation",
        }),
        updateVisitorLang() {
            sendSocketMessage("visitor lang-change", {
                visitorId: this.activeConversation.visitor._id,
                siteId: this.site._id,
                preferredLang: this.activeConversation.visitor.preferredLang,
                conversationId: this.conversationId,
            });
        },
        getVisitorAvatarSrc() {
            if (this.activeConversation.whatsappNumber) {
                return `${CONFIG.WHATSAPP.avatarAddress}?contactId=${this.activeConversation.whatsappNumber}&siteId=${
                    this.site._id
                }&token=${getPersistenceData("Admin-Token")}&r=${Math.random() * 1000}`;
            }
            return undefined;
        },
        getAgentNames(chat) {
            return chat.users
                .map((id) => {
                    const u = this.users.find((x) => x._id === id);
                    return u ? u.name : "";
                })
                .join(",");
        },
        getCustomLoginInfos() {
            let inputValues = this.activeConversation.customLoginInfos || [];
            log.debug("getCustomLoginInfos called", {
                inputs: this.inputs,
                inputValues,
            });
            inputValues = inputValues
                .filter((x) => this.inputs[x.input])
                .filter((x) => !["email", "fullname"].includes(this.inputs[x.input].type))
                .map((v) => ({
                    value: this.inputs[v.input].type === "subject" ? this.getSubjectName(v.value) : v.value,
                    name: this.inputs[v.input].localizedLabel[this.$i18n.locale].text,
                }));
            return inputValues;
        },
        getSubjectName(id) {
            let maybeSubject = this.subjects.find((x) => x._id == id);
            return maybeSubject ? maybeSubject.languageContent[i18n.locale].name : undefined;
        },
        getUserDetailedLoginInfo(uaString) {
            const parser = require("ua-parser-js");
            const ua = parser(uaString || "-");
            return `${ua.os.name} ${ua.os.version} | ${ua.browser.name} ${ua.browser.major}`;
        },
        copyUsername(e) {
            const range = document.createRange();
            range.selectNode(e.currentTarget.previousElementSibling);
            window.getSelection().removeAllRanges();
            window.getSelection().addRange(range);
            document.execCommand("copy");
            window.getSelection().removeAllRanges();
        },
        copyTextToClipboard(str) {
            const el = document.createElement("textarea");
            el.value = str;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            this.$bvToast.toast(this.$t("message.copiedClipboard"), {
                title: this.$t("message.copiedClipboard"),
                variant: "success",
                solid: true,
            });
        },
        getCountryCode(country) {
            country = country.trim().toLowerCase();
            if (country.length <= 3) {
                return country;
            } else {
                const maybeCounty = countryCodes.find((x) => x.name.toLowerCase().trim() === country);
                return maybeCounty ? maybeCounty.code : undefined;
            }
        },
        getCountryName(country = "") {
            const q = country.toLowerCase().trim();
            const maybeCountry = countryCodes.find(
                (x) => x.name.toLowerCase().trim() === q || x.code.toLowerCase().trim() === q
            );
            return maybeCountry ? maybeCountry.name : country;
        },
        saveInfo() {
            let payload = {
                id: this.activeConversation._id,
                representativeNote: this.info,
                representativeName: this.user.name,
            };
            log("save info", payload);
            this.addNoteToConversation(payload).then((response) => {
                this.noteSender = this.activeConversation.note.representativeName;
                this.info = this.activeConversation.note.representativeNote;
                this.isEditingOpen = false;
            });
        },
        notSaveInfo() {
            this.info = this.activeConversation.note.representativeNote;
            this.isEditingOpen = false;
        },
        setVisitorEmailInputActive(active) {
            this.visitorEmailInputActive = active;
            if (!active) {
                let email = this.$refs.visitorEmailInput.textContent.toString();

                email = email.toLowerCase().trim().replace(this.$t("message.addEmail").toLowerCase().trim(), "").trim();
                if (email.length === 0) {
                    this.renderCount += 1;
                    return;
                }
                const c = this.activeConversation;
                if (validateEmail(email)) {
                    if (c && c.email !== email) {
                        const visitorId = typeof c.visitor === "object" ? c.visitor._id : c.visitor;
                        log("email updating", email);
                        sendSocketMessage("user visitor-info-updated", {
                            siteId: this.site._id,
                            conversationId: this.activeConversation._id,
                            visitorId,
                            fullname: c.fullname,
                            email,
                        });
                        this.$toasted.show(this.$t("message.visitorEmailUpdated"), {
                            duration: 3000,
                            position: "top-center",
                        });
                    }
                } else {
                    this.$toasted.error(this.$t("message.visitorEmailIsNotValid"), {
                        duration: 3000,
                        position: "top-center",
                    });
                    this.$refs.visitorEmailInput.textContent = c.email === "" ? this.$t("message.addEmail") : c.email;
                }
                this.renderCount += 1;
            }
        },
        focusVisitorEmailInput() {
            if (this.$nextTick) {
                this.$nextTick(() => {
                    if (this.$refs.visitorEmailInput) {
                        this.$refs.visitorEmailInput.focus();
                        const c = this.activeConversation;
                        if (c.email === "") {
                            this.$refs.visitorEmailInput.innerHTML = "";
                        }
                    }
                });
            }
        },
        handleVisitorEmailInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorEmailInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        setVisitorNameInputActive(active) {
            log("setVisitorNameInputActive", active);
            this.visitorNameInputActive = active;
            if (!active) {
                let fullname = this.$refs.visitorNameInput.textContent.toString();
                log("fullname before", "" + fullname);
                fullname = (fullname || "")
                    .trim()
                    .toLowerCase()
                    .split(" ")
                    .map((x) => x.trim())
                    .filter((x) => x.length > 0)
                    .map((x) => capitalize(x))
                    .join(" ");
                log("fullname after", fullname);
                const c = this.activeConversation;
                if (!fullname.length) {
                    this.renderCount += 1;
                    return;
                }
                if (c && c.fullname !== fullname) {
                    const visitorId = typeof c.visitor === "object" ? c.visitor._id : c.visitor;
                    log("fullname updating", fullname);
                    sendSocketMessage("user visitor-info-updated", {
                        siteId: this.site._id,
                        conversationId: this.activeConversation._id,
                        visitorId,
                        fullname,
                        email: c.email,
                    });
                    this.$toasted.show(this.$t("message.visitorNameUpdated"), {
                        duration: 3000,
                        position: "top-center",
                    });
                } else {
                    this.renderCount += 1;
                }
            }
        },
        focusVisitorNameInput() {
            if (this.$nextTick) {
                this.$nextTick(() => {
                    if (this.$refs.visitorNameInput) this.$refs.visitorNameInput.focus();
                });
            }
        },
        handleVisitorNameInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorNameInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
    },
};
</script>

<style lang="scss">
.new-theme {
    #information-list2 {
        box-shadow: 0 0 3px #00000080;
        z-index: 9;
        background-color: #ffffff;
        padding: 20px;
        margin-bottom: 0px;
        height: calc(100vh - 180px);
        border-radius: 6px;
        overflow-y: auto;
    }

    .headtitle {
        font-size: 18px;
        color: #101217;
        /*   padding-bottom: 5px;*/
    }

    .userInfo {
        font-family: Poppins;

        &.first {
            color: #3699ff;
            font-size: 16px;
            font-weight: 500;
            padding-top: 15px;
            line-height: 1;
        }

        &.sec {
            color: #181c32;
            font-size: 14px;
            font-weight: 300;

            .rep-note-element {
                position: relative;
                margin-top: 5px;

                .rep-note-name {
                    position: absolute;
                    top: -20px;
                    right: 5px;
                    font-weight: 200;
                    font-style: italic;
                }

                .rep-note-actions {
                    float: right;
                    margin-top: 6px;
                }
            }
        }
    }
}
</style>
